// Changing a tile's size: dragging one of its corners to any whole number of cells, or with Shift to the sizes its kind
// offers (startResize, from client/lib/input.js; the grips are drawn by addGrips), and what the Size menu shares with that,
// setTileSize. A mouse has the grips while hovering a tile; a finger or pen only while arranging, when the tiles jiggle.
import { SCALE } from '../widgets/kit.js';
import { dropRoom, endPreview, roomPreview } from './arrange.js';
import {
  autoOn, band, cellsOf, COLUMNS, EDGE, fitX, GRID, moveTo, pinDrawn, screenW, setSizeKey, settle, sizeIn, sizeKey, sizeOf, sizesFor, sizesPerScreen, snap, snapOn, spotOf, TILE_GAP, tilePos, viewportOn,
} from './geometry.js';
import { DRAG_THRESHOLD } from './input.js';
import { fitTile, relayout, render, setSelection } from './render.js';
import { API, byId, currentRole, $webtop } from './state.js';
import { api, toast } from './ui.js';

const CORNERS = ['nw', 'ne', 'sw', 'se'];
// Run for every layout of a tile, after whatever its widget drew, which may have replaced its children.
export function addGrips(el, s) {
  if (currentRole() !== 'editor' || el.querySelector(':scope > .tile-grip')) return;
  el.append(...CORNERS.map((corner) => {
    const grip = Object.assign(document.createElement('div'), { className: 'tile-grip' });
    grip.dataset.corner = corner;
    return grip;
  }));
}

const ROWS_MAX = 12; // isSize, model.js
// The whole number of cells nearest w x h, no wider than the screen.
function freeSize(w, h) {
  const across = Math.floor((screenW() - 2 * EDGE + TILE_GAP) / GRID.w);
  const cells = (v, step, most) => Math.max(1, Math.min(Math.round((v + TILE_GAP) / step), most));
  return `${cells(w, GRID.w, Math.max(1, Math.min(across, COLUMNS.l)))}x${cells(h, GRID.h, ROWS_MAX)}`;
}

let cancel = null; // ends the resize in progress without saving it
export const resizing = () => Boolean(cancel);
export const cancelResize = () => cancel?.();

// The corner opposite the one dragged stays put, and the tile takes the whole number of cells nearest the pointer,
// whichever its kind offers, or with Shift held the size it offers that is nearest, its own winning a tie (a widget wider
// than the screen is drawn the same at several). Everything is decided by that size, so the tile is only redrawn when it
// changes. While dragged, the size is on the record and goes back unless saved.
export function startResize(e, tile, corner) {
  const s = byId(tile.dataset.id);
  if (!s) return;
  e.preventDefault();
  tile.focus();
  setSelection([s.id]);
  const before = { size: s.size, layouts: s.layouts };
  const from = tilePos(tile), was = sizeOf(s), start = sizeKey(s), startScroll = scrollY;
  const west = corner.includes('w'), north = corner.includes('n');
  const room = viewportOn() && snapOn(), auto = autoOn();
  const sizes = [start, ...sizesFor(s)];
  // A widget wider than the screen is drawn narrower than its size, which a drag that leaves that width alone must keep.
  const ownCols = cellsOf(start)[0], drawnCols = cellsOf(freeSize(was.w, was.h))[0];
  let key = start, spot = from, active = false, changed = false;
  const restore = () => {
    s.size = before.size;
    if (before.layouts) s.layouts = before.layouts; else delete s.layouts;
  };
  const nearest = (w, h) => {
    const off = (k) => { const size = sizeIn(s, k); return Math.hypot(size.w - w, size.h - h); };
    return sizes.reduce((best, k) => (off(k) < off(best) ? k : best));
  };
  const free = (w, h) => {
    const [cols, rows] = cellsOf(freeSize(w, h));
    return `${cols === drawnCols ? ownCols : cols}x${rows}`;
  };
  const take = (next) => {
    key = next;
    changed = true;
    setSizeKey(s, key);
    if (auto) return relayout(); // the packing decides where it goes, and shows what the drop will
    const size = sizeOf(s);
    spot = key === start ? from : snap(from.x + (west ? was.w - size.w : 0), from.y + (north ? was.h - size.h : 0), size, false, true);
    fitTile(tile, s, spot);
    if (room) roomPreview(new Map([[s.id, spot]]));
  };
  const move = (ev) => {
    if (ev.pointerId !== e.pointerId) return;
    const dx = ev.clientX - e.clientX, dy = ev.clientY - e.clientY + scrollY - startScroll;
    if (!active && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    active = true;
    tile.classList.add('dragging');
    const w = was.w + (west ? -dx : dx) / SCALE / fitX(), h = was.h + (north ? -dy : dy) / SCALE;
    const next = ev.shiftKey ? nearest(w, h) : free(w, h);
    if (next !== key) take(next);
  };
  const finish = () => {
    cancel = null;
    removeEventListener('pointermove', move);
    removeEventListener('pointerup', end);
    removeEventListener('pointercancel', end);
    tile.classList.remove('dragging');
    endPreview();
  };
  const end = async (ev) => {
    if (ev.pointerId !== e.pointerId) return;
    finish();
    if (ev.type !== 'pointerup' || key === start) {
      restore();
      if (changed) render();
      return;
    }
    if (!room) { // setTileSize takes it from the size it had
      restore();
      return setTileSize(s, key, auto ? undefined : spot);
    }
    // A drag on the Viewport layout's grid makes room like moving a tile does (Size in the menu never moves another).
    await dropRoom(new Map([[s.id, spot]]));
    try {
      await api('PATCH', `${API}/shortcuts/${s.id}`, band() === 'l' ? { size: key } : { layouts: s.layouts });
    } catch (err) { restore(); render(); toast(err); }
  };
  cancel = () => { finish(); restore(); };
  tile.setPointerCapture(e.pointerId);
  // On the window: a redraw in the middle of a drag takes the grip and the capture with it, and the drag must still end.
  addEventListener('pointermove', move);
  addEventListener('pointerup', end);
  addEventListener('pointercancel', end);
}

// With the Viewport switch on the size is this screen's. In the Viewport layout no other tile moves: the one resized goes
// to the nearest free spot if it no longer fits where it is, or `at` (a dragged corner's). Auto Arrange just packs again.
async function setViewportSize(s, size, at) {
  const before = { size: s.size, x: s.x, y: s.y, layouts: s.layouts };
  const b = band();
  setSizeKey(s, size);
  const own = viewportOn();
  const el = own && $webtop.querySelector(`.tile[data-id="${s.id}"]`);
  if (el) {
    const taken = [...$webtop.querySelectorAll('.tile')].filter((t) => t !== el).map((t) => ({ ...tilePos(t), ...sizeOf(byId(t.dataset.id)) }));
    moveTo(s, settle(at ?? tilePos(el), taken, sizeOf(s), !snapOn()));
  }
  const pins = pinDrawn([s]);
  render([s]);
  try {
    if (pins.length) await api('PATCH', `${API}/shortcuts`, pins);
    await api('PATCH', `${API}/shortcuts/${s.id}`, b === 'l' ? { size, ...(own && spotOf(s)) } : { layouts: s.layouts });
  } catch (e) { Object.assign(s, before); if (!before.layouts) delete s.layouts; render(); toast(e); }
}
// `at`: where the tile goes (a dragged corner's); otherwise it keeps its top-left corner.
export async function setTileSize(s, size, at) {
  if (sizeKey(s) === size) return;
  if (sizesPerScreen()) return setViewportSize(s, size, at);
  const before = { size: s.size, x: s.x, y: s.y };
  Object.assign(s, { size, ...at });
  render([s]); // this tile keeps its place, the others move out of its way (display only)
  try { await api('PATCH', `${API}/shortcuts/${s.id}`, { size, ...at }); } catch (e) { Object.assign(s, before); render(); toast(e); }
}
